import { Flex, Stack, Skeleton } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import Image from 'next/image';
import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';
import type { StrapiImage } from '@betterpool/strapi-client';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import useStyles from './SlideshowCard.styles';

type SlideshowCardProps = {
  title?: string;
  copy?: string;
  imageDesktop?: StrapiImage;
  children?: React.ReactNode;
};

export function SlideshowCard({ title, copy, imageDesktop, children }: SlideshowCardProps) {
  const isTablet = useIsTablet();
  const { classes } = useStyles();
  return (
    <Flex /* eslint-disable-line local-rules/use-theme-tokens */ gap={40} direction="column" w="100%" h="100%">
      <Stack>
        <div className={classes.slideshowCardWrapper}>
          <Title
            variant={isTablet ? 'display-headline-expanded-xl' : 'display-headline-expanded-xxl'}
            className={classes.title}
            c="gray2.6"
          >
            {title}
          </Title>
          <Text
            variant={isTablet ? 'body-large' : 'body-x-large'}
            className={classes.copy}
            c="gray2.4"
          >
            {copy}
          </Text>
        </div>
        {children}
        {imageDesktop?.url.length && (
          <div className={classes.imageWrapper}>
            <Image
              src={getCdnUrl(imageDesktop.url)}
              fill
              objectFit="contain"
              alt={title}
              className={classes.image}
            />
          </div>
        )}
      </Stack>
    </Flex>
  );
}

export function SlideshowCardSkeleton() {
  const { classes, cx } = useStyles();
  const isTablet = useIsTablet();
  return (
    <Flex /* eslint-disable-line local-rules/use-theme-tokens */ gap={40} direction="column" w="100%" h="100%">
      <Stack>
        <div className={classes.slideshowCardWrapper}>
          <Skeleton height={42} width="100%" className={classes.skeletonTitle}>
            <div className={cx(classes.title, classes.skeletonText)}>&nbsp;</div>
          </Skeleton>
          <Skeleton height={70} width="100%">
            <div className={cx(classes.copy, classes.skeletonText)}>&nbsp;</div>
          </Skeleton>
        </div>
        <div className={classes.imageWrapper}>
          <Skeleton
            height={isTablet ? 530 : 650}
            width={isTablet ? '80%' : 320}
            className={classes.imageWrapper}
          >
            <div className={cx(classes.image, classes.skeletonImage)}>&nbsp;</div>
          </Skeleton>
        </div>
      </Stack>
    </Flex>
  );
}
